import React from "react";
import { Box } from "@mui/material";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import { BUTTON_RESET, C, FONT_BODY, FONT_DISPLAY, FOCUS_RING, RADIUS, SHADOW, TYPE } from "../../../utils/designTokens";
import { resizedImage, resizedSrcSet } from "../../../utils/resizedImage";

/** A status chip: soft pill with an optional icon. tone: neutral | open | closed | warn. */
export function StatusChip({ icon: Icon, children, tone = "neutral" }) {
  const tones = {
    neutral: { bg: C.surface, color: C.ink, border: C.border },
    open: { bg: C.greenSoft, color: C.green, border: "transparent" },
    closed: { bg: C.surface2, color: C.ink, border: "transparent" },
    warn: { bg: C.redSoft, color: C.redLink, border: "transparent" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <Box
      component="li"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 32,
        px: 1.5,
        borderRadius: RADIUS.pill,
        bgcolor: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        fontFamily: FONT_BODY,
        fontSize: 13,
        fontWeight: tone === "open" ? 700 : 500,
        whiteSpace: "nowrap",
        listStyle: "none",
      }}
    >
      {tone === "open" ? (
        <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: C.green }} />
      ) : Icon ? (
        <Icon aria-hidden="true" sx={{ fontSize: 16, color: tone === "neutral" ? C.muted : "inherit" }} />
      ) : null}
      {children}
    </Box>
  );
}

const roundButton = {
  ...BUTTON_RESET,
  width: 44,
  height: 44,
  borderRadius: "50%",
  bgcolor: C.bg,
  color: C.ink,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: SHADOW.button,
  transition: "transform .15s ease",
  "&:hover": { transform: "scale(1.05)" },
  ...FOCUS_RING,
};

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/**
 * Hero photo with Favourite / Share, and the white info card overlapping its
 * bottom: logo tile, name, cuisines, status chips and Store info.
 *
 *   status  — { label, tone } for the first chip ("Open · until 8:00 PM")
 *   chips   — [{ key, icon, label, tone }] after it (hours, ETA, fee)
 *   actions — extra buttons beside Store info (e.g. Reserve a table)
 */
function RestaurantHero({
  name,
  image,
  logo,
  cuisineLine,
  isFoodTruck = false,
  status,
  chips = [],
  isFavourite = false,
  onToggleFavourite,
  onShare,
  onStoreInfo,
  storeInfoLabel = "Store info",
  actions = null,
}) {
  return (
    <Box component="header" sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "relative",
          height: { xs: 220, sm: 260, md: 300 },
          borderRadius: RADIUS.hero,
          overflow: "hidden",
          bgcolor: C.surface2,
        }}
      >
        {image && (
          <Box
            component="img"
            src={resizedImage(image, 1200)}
            srcSet={resizedSrcSet(image, [480, 800, 1200])}
            sizes="(min-width: 1200px) 860px, 100vw"
            alt=""
            fetchpriority="high"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        {isFoodTruck && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              height: 32,
              px: 1.5,
              borderRadius: RADIUS.pill,
              bgcolor: C.green,
              color: "#FFFFFF",
              fontFamily: FONT_BODY,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 17 }} />
            Food truck
          </Box>
        )}
        <Box sx={{ position: "absolute", top: 16, right: 16, display: "flex", gap: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={onToggleFavourite}
            aria-pressed={isFavourite}
            aria-label={isFavourite ? `Remove ${name} from favourites` : `Add ${name} to favourites`}
            sx={roundButton}
          >
            {isFavourite ? (
              <FavoriteRoundedIcon sx={{ fontSize: 22, color: C.red }} />
            ) : (
              <FavoriteBorderRoundedIcon sx={{ fontSize: 22 }} />
            )}
          </Box>
          <Box component="button" type="button" onClick={onShare} aria-label={`Share ${name}`} sx={roundButton}>
            <IosShareRoundedIcon sx={{ fontSize: 21 }} />
          </Box>
        </Box>
      </Box>

      {/* Info card, overlapping the hero's bottom by 64px. */}
      <Box
        sx={{
          position: "relative",
          mt: "-64px",
          mx: { xs: 1.5, sm: 2.5, md: 3 },
          p: { xs: 2, md: 3 },
          borderRadius: RADIUS.card,
          bgcolor: C.bg,
          boxShadow: SHADOW.float,
          display: "flex",
          flexWrap: { xs: "wrap", md: "nowrap" },
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            width: { xs: 64, md: 88 },
            height: { xs: 64, md: 88 },
            flexShrink: 0,
            borderRadius: RADIUS.menuCard,
            border: `1px solid ${C.border}`,
            bgcolor: C.bg,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {logo ? (
            <Box component="img" src={resizedImage(logo, 240)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Box component="span" aria-hidden="true" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 22, md: 28 }, color: C.red }}>
              {initialsOf(name)}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, flexBasis: { xs: "calc(100% - 80px)", md: "auto" } }}>
          <Box component="h1" sx={{ ...TYPE.pageTitle, fontSize: { xs: 26, md: 34 }, m: 0, color: C.ink, overflowWrap: "anywhere" }}>
            {name}
          </Box>
          {cuisineLine && (
            <Box component="p" sx={{ ...TYPE.bodyStrong, m: 0, mt: 0.5, color: C.muted }}>
              {cuisineLine}
            </Box>
          )}
          <Box component="ul" aria-label="Store status" sx={{ display: "flex", flexWrap: "wrap", gap: 1, m: 0, mt: 1.5, p: 0 }}>
            {status && (
              <StatusChip tone={status.tone} icon={status.tone === "closed" ? ScheduleRoundedIcon : null}>
                {status.label}
              </StatusChip>
            )}
            {chips.map((c) => (
              <StatusChip key={c.key} icon={c.icon} tone={c.tone}>
                {c.label}
              </StatusChip>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignSelf: "center", width: { xs: "100%", md: "auto" } }}>
          {actions}
          <Box
            component="button"
            type="button"
            onClick={onStoreInfo}
            sx={{
              ...BUTTON_RESET,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              height: 44,
              px: 2,
              borderRadius: RADIUS.control,
              border: `1px solid ${C.borderStrong}`,
              color: C.ink,
              fontFamily: FONT_BODY,
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: "nowrap",
              flex: { xs: 1, md: "none" },
              "&:hover": { bgcolor: C.surface },
              ...FOCUS_RING,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 19 }} />
            {storeInfoLabel}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Icons the page uses for the hours / ETA / fee chips.
export const CHIP_ICONS = {
  hours: AccessTimeRoundedIcon,
  eta: ScheduleRoundedIcon,
  delivery: DeliveryDiningOutlinedIcon,
  pickup: StorefrontOutlinedIcon,
};

export default React.memo(RestaurantHero);
